import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, Input, Button, Space, Typography, Alert, Modal } from 'antd';
import {
    SearchOutlined,
    SafetyCertificateOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import {
    FINO_IMW_GET_SENDER_LIMIT,
    POST_DMT_REFRESH_SERVICE,
    GET_MYPROFILE,
    DMT_SETTING_URL,
} from './../../Utils/appConstants';
import { isNumber } from './../../Utils/Common';
import { postRequest, getRequest } from './../../Utils/AppApi';
import FinoImWDMT from '../finodmtImw/index';
import AirtelImwDMT from '../moneyTransferV2/index';

import airtelIcon from './../../assets/airtel_icon.png';
import finoIcon from './../../assets/fino_logo.png';

const { Text } = Typography;

// ---- DMT setting codes ----
// DMT1 -> Airtel (IP) | DMT3 -> Fino_IMW
const OPERATOR_DMT_CODE = {
    airtel: 'DMT1',
    fino: 'DMT3',
};

// ---- Service code sent to the Refresh DMT API per operator ----
const OPERATOR_REFRESH_SERVICE_CODE = {
    airtel: 'DMT1',
};

// ----- Validation Helpers -----
const isRepeatedDigits = (mobile) => /^(\d)\1{9}$/.test(mobile);

const isSequentialDigits = (mobile) => {
    const digits = mobile.split('').map(Number);
    const ascending = digits.every((d, i) => i === 0 || d === digits[i - 1] + 1);
    const descending = digits.every((d, i) => i === 0 || d === digits[i - 1] - 1);
    return ascending || descending;
};

const isValidMobile = (mobile) => {
    if (!/^[6-9]\d{9}$/.test(mobile)) {
        return { valid: false, message: 'Invalid mobile number. Must be a 10-digit Indian number starting with 6, 7, 8, or 9.' };
    }
    if (isRepeatedDigits(mobile)) {
        return { valid: false, message: 'Invalid mobile number. Repeated digits (e.g., 9999999999) are not allowed.' };
    }
    if (isSequentialDigits(mobile)) {
        return { valid: false, message: 'Invalid mobile number. Sequential digits (e.g., 1234567890) are not allowed.' };
    }
    return { valid: true, message: '' };
};

const IndexComponent = () => {
    const history = useHistory();
    const [isLoading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showDashboard, setShowDashboard] = useState(false);
    const [verifiedMobile, setVerifiedMobile] = useState('');
    const [operatorModalVisible, setOperatorModalVisible] = useState(false);
    const [operatorData, setOperatorData] = useState({ airtel: null, fino: null });
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [tempSelected, setTempSelected] = useState(null);

    // Data extracted from the Airtel Refresh DMT response, forwarded to AirtelImwDMT
    const [airtelContext, setAirtelContext] = useState({
        validity: '',
        referenceKey: '',
        limitAvailable: '',
    });

    // DMT settings from backend { [code]: { flag, message, ... } }
    const [dmtSettings, setDmtSettings] = useState({});
    const [dmtSettingsLoading, setDmtSettingsLoading] = useState(true);

    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // ---- Load DMT settings once ----
    useEffect(() => {
        const fetchDmtSettings = async () => {
            try {
                const response = await getRequest(DMT_SETTING_URL, history);
                if (response?.success && Array.isArray(response.data)) {
                    const map = {};
                    response.data.forEach((item) => {
                        if (item?.code) map[item.code] = item;
                    });
                    setDmtSettings(map);
                }
            } catch (err) {
                console.error('Failed to load DMT settings:', err);
            } finally {
                setDmtSettingsLoading(false);
            }
        };
        fetchDmtSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Helper: is a given operator disabled? ----
    const getOperatorAvailability = (operatorKey) => {
        const code = OPERATOR_DMT_CODE[operatorKey];
        const setting = code ? dmtSettings[code] : null;
        if (!setting) return { disabled: false, message: '' };
        const disabled = setting.flag === false;
        return {
            disabled,
            message: disabled
                ? (setting.message || 'This service is currently under maintenance. Please try again later.')
                : '',
        };
    };

    // ---- Extract validity / referenceKey / limitAvailable for Airtel ----
    const extractAirtelContext = (response) => {
        if (!response || response.success !== true) {
            return { validity: '', referenceKey: '', limitAvailable: '' };
        }
        const cd = response.data?.customerData || {};
        return {
            validity: cd.validity || '',
            referenceKey: cd.referenceKey || '',
            limitAvailable: cd.limitAvailable || response.data?.remainingLimit || '',
        };
    };

    const handleSearch = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setSearchText(value);
        setErrorMessage('');
        if (showDashboard) {
            setShowDashboard(false);
            setVerifiedMobile('');
            setSelectedOperator(null);
        }
    };

    const clearInput = () => {
        setSearchText('');
        setErrorMessage('');
        inputRef.current?.focus();
    };

    const checkBothOperators = async (mobile) => {
        const validation = isValidMobile(mobile);
        if (!validation.valid) {
            setErrorMessage(validation.message);
            return;
        }

        // ---- Block submission if all DMT services are disabled ----
        const airtelAvail = getOperatorAvailability('airtel');
        const finoAvail = getOperatorAvailability('fino');
        if (airtelAvail.disabled && finoAvail.disabled) {
            setErrorMessage(
                airtelAvail.message ||
                finoAvail.message ||
                'All DMT services are currently under maintenance. Please try again later.'
            );
            return;
        }

        setLoading(true);
        setErrorMessage('');

        const latitude = localStorage.getItem('latitude') || '0.0';
        const longitude = localStorage.getItem('longitude') || '0.0';

        // ---- Airtel now uses Refresh DMT (POST_DMT_REFRESH_SERVICE) ----
        const airtelPayload = {
            customerMobile: mobile,
            serviceCode: OPERATOR_REFRESH_SERVICE_CODE.airtel, // 'DMT1'
            latitude,
            longitude,
        };

        // ---- Fino keeps its own endpoint ----
        const finoPayload = {
            senderMobile: mobile,
            latitude,
            longitude,
        };

        try {
            const [finoResult, airtelResult] = await Promise.allSettled([
                postRequest(FINO_IMW_GET_SENDER_LIMIT, finoPayload, history),
                postRequest(POST_DMT_REFRESH_SERVICE, airtelPayload, history),
            ]);

            const finoResponse = finoResult.status === 'fulfilled' ? finoResult.value : null;
            const airtelResponse = airtelResult.status === 'fulfilled' ? airtelResult.value : null;

            setOperatorData({ fino: finoResponse, airtel: airtelResponse });

            // Extract and store airtel context (validity, referenceKey, limitAvailable)
            setAirtelContext(extractAirtelContext(airtelResponse));

            setVerifiedMobile(mobile);
            setTempSelected(null);
            setOperatorModalVisible(true);

            if (!finoResponse && !airtelResponse) {
                setErrorMessage('Both operators are unavailable. Please try again.');
                setOperatorModalVisible(false);
            }
        } catch (error) {
            console.error('Operator check error:', error);
            setErrorMessage('Server error – please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const selectOperatorInModal = (operator) => {
        setTempSelected(operator);
    };

    const confirmOperator = () => {
        if (!tempSelected) return;
        const avail = getOperatorAvailability(tempSelected);
        if (avail.disabled) {
            toast.error(avail.message || 'This service is under maintenance.');
            return;
        }
        setOperatorModalVisible(false);
        setSelectedOperator(tempSelected);
        setShowDashboard(true);
        toast.success(`${tempSelected === 'airtel' ? 'Airtel' : 'Fino'} DMT selected`);
    };

    const goBackToSearch = () => {
        setShowDashboard(false);
        setVerifiedMobile('');
        setSearchText('');
        setErrorMessage('');
        setSelectedOperator(null);
        setTempSelected(null);
        setAirtelContext({ validity: '', referenceKey: '', limitAvailable: '' });
        inputRef.current?.focus();
    };

    const renderOperatorModal = () => {
        const { fino, airtel } = operatorData;

        // ---- Airtel parse: Refresh DMT response shape ----
        const parseAirtelStatus = (response) => {
            if (!response || response.success !== true) {
                return { registered: false, limit: 'N/A' };
            }
            const data = response.data;
            if (!data) return { registered: false, limit: 'N/A' };

            const registered =
                (data.customerStatus || '').toLowerCase() === 'registered';

            let limitVal = data.remainingLimit;
            if (limitVal === undefined || limitVal === null) {
                limitVal = data.customerData?.limitAvailable;
            }

            let limit = 'N/A';
            if (limitVal !== undefined && limitVal !== null && !isNaN(Number(limitVal))) {
                limit = `₹${Number(limitVal).toFixed(2)}`;
            }
            return { registered, limit };
        };

        // ---- Fino parse: original shape ----
        const parseFinoStatus = (response) => {
            if (!response) return { registered: false, limit: 'N/A' };
            const registered = response.isRegistered === true;
            let limit = 'N/A';
            const limitVal = response.limit ?? response.data?.limit ?? response.data?.limitAvailable;
            if (limitVal !== undefined && limitVal !== null) {
                limit = `₹${Number(limitVal).toFixed(2)}`;
            }
            return { registered, limit };
        };

        const finoStatus = parseFinoStatus(fino);
        const airtelStatus = parseAirtelStatus(airtel);

        const airtelAvail = getOperatorAvailability('airtel');
        const finoAvail = getOperatorAvailability('fino');

        const handleCardClick = (key, avail) => {
            if (avail.disabled || dmtSettingsLoading) return;
            selectOperatorInModal(key);
        };

        return (
            <Modal
                title="Select Operator"
                open={operatorModalVisible}
                footer={null}
                closable={false}
                maskClosable={false}
                centered
                width={420}
                bodyStyle={{ padding: '20px' }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Airtel Card */}
                    <div
                        onClick={() => handleCardClick('airtel', airtelAvail)}
                        style={{
                            padding: '16px',
                            borderRadius: '12px',
                            background: airtelAvail.disabled
                                ? '#f1f5f9'
                                : tempSelected === 'airtel'
                                    ? '#eef2ff'
                                    : '#f8fafc',
                            border: `2px solid ${
                                airtelAvail.disabled
                                    ? '#cbd5e1'
                                    : tempSelected === 'airtel'
                                        ? '#5851D2'
                                        : airtelStatus.registered
                                            ? '#22c55e'
                                            : '#e2e8f0'
                            }`,
                            cursor: airtelAvail.disabled ? 'not-allowed' : 'pointer',
                            opacity: airtelAvail.disabled ? 0.75 : 1,
                            transition: 'all 0.2s',
                            boxShadow:
                                tempSelected === 'airtel' && !airtelAvail.disabled
                                    ? '0 0 0 4px rgba(88,81,210,0.15)'
                                    : '0 2px 4px rgba(0,0,0,0.04)',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <img
                                    src={airtelIcon}
                                    alt="Airtel"
                                    style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                                />
                                <Text strong style={{ fontSize: '16px' }}>Airtel DMT</Text>
                            </div>
                            {airtelAvail.disabled ? (
                                <Text style={{ color: '#ef4444', fontWeight: 600 }}>
                                    🔧 Maintenance
                                </Text>
                            ) : (
                                <Text
                                    style={{
                                        color: airtelStatus.registered ? '#22c55e' : '#ef4444',
                                        fontWeight: 600,
                                    }}
                                >
                                    {airtelStatus.registered ? '✅ Registered' : '❌ Not Registered'}
                                </Text>
                            )}
                        </div>
                        <div
                            style={{
                                marginTop: '8px',
                                fontSize: '14px',
                                color: airtelAvail.disabled ? '#ef4444' : '#64748b',
                            }}
                        >
                            {airtelAvail.disabled
                                ? airtelAvail.message
                                : `Limit: ${airtelStatus.limit}`}
                        </div>
                    </div>

                    {/* Fino Card — uncomment when Fino DMT is enabled */}
                    {/* <div ... /> */}

                    <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                        <Button
                            onClick={() => setOperatorModalVisible(false)}
                            style={{ flex: 1 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="primary"
                            onClick={confirmOperator}
                            disabled={!tempSelected}
                            style={{
                                flex: 1,
                                background: tempSelected
                                    ? 'linear-gradient(135deg, #5851D2 0%, #6d66e0 100%)'
                                    : undefined,
                                border: 'none',
                            }}
                        >
                            Continue
                        </Button>
                    </div>
                </div>
            </Modal>
        );
    };

    useEffect(() => {
        const getProfile = async () => {
            try {
                const response = await getRequest(GET_MYPROFILE, history);
                if (response.success) {
                    // setUserProfile(response.data[0]);
                }
            } catch (_) { }
        };
        getProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            style={{
                minHeight: '100vh',
                background: 'linear-gradient(165deg, #f8f9fc 0%, #f1f0f9 45%, #f7f5ff 100%)',
                padding: '20px 10px 50px',
            }}
        >
            <div style={{ width: '100%' }}>
                {!showDashboard ? (
                    <Card
                        bordered={false}
                        style={{
                            borderRadius: 18,
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.04), 0 12px 28px -4px rgba(0,0,0,0.07)',
                            background: '#ffffff',
                            border: '1px solid rgba(0,0,0,0.03)',
                        }}
                        bodyStyle={{ padding: '22px 20px' }}
                    >
                        <Space direction="vertical" size={24} style={{ width: '100%' }}>
                            <div>
                                <Text
                                    style={{
                                        fontSize: 14,
                                        color: '#3020be',
                                        fontWeight: 600,
                                        letterSpacing: '0.5px',
                                        textTransform: 'uppercase',
                                    }}
                                >
                                    Sender Mobile Number
                                </Text>
                                <Text
                                    style={{
                                        display: 'block',
                                        marginTop: 4,
                                        fontSize: 14,
                                        color: '#64748b',
                                        fontWeight: 400,
                                    }}
                                >
                                    Enter the 10‑digit mobile number to verify the sender
                                </Text>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 12,
                                    background: '#f8fafc',
                                    borderRadius: 14,
                                    padding: 8,
                                    border: `1.5px solid ${errorMessage ? '#fca5a5' : '#e2e8f0'}`,
                                    transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
                                    boxShadow: errorMessage
                                        ? '0 0 0 4px rgba(248,113,113,0.12)'
                                        : '0 2px 4px rgba(0,0,0,0.02)',
                                }}
                            >
                                <Input
                                    ref={inputRef}
                                    size="large"
                                    placeholder="9876543210"
                                    value={searchText}
                                    onChange={handleSearch}
                                    onKeyDown={isNumber}
                                    onPressEnter={!showDashboard ? () => checkBothOperators(searchText) : undefined}
                                    prefix={
                                        <SearchOutlined style={{ color: '#94a3b8', fontSize: 18, marginRight: 6 }} />
                                    }
                                    suffix={
                                        searchText ? (
                                            <CloseCircleOutlined
                                                onClick={clearInput}
                                                style={{ color: '#94a3b8', fontSize: 18, cursor: 'pointer', transition: 'color 0.15s' }}
                                                onMouseEnter={(e) => (e.currentTarget.style.color = '#64748b')}
                                                onMouseLeave={(e) => (e.currentTarget.style.color = '#94a3b8')}
                                            />
                                        ) : null
                                    }
                                    maxLength={10}
                                    bordered={false}
                                    disabled={isLoading}
                                    style={{
                                        flex: 1,
                                        minWidth: 180,
                                        background: 'transparent',
                                        fontSize: 18,
                                        fontWeight: 500,
                                        letterSpacing: '0.5px',
                                        color: '#1e293b',
                                        height: 48,
                                    }}
                                />

                                <Button
                                    type="primary"
                                    size="large"
                                    onClick={() => checkBothOperators(searchText)}
                                    loading={isLoading}
                                    disabled={searchText.length !== 10}
                                    icon={<SafetyCertificateOutlined />}
                                    style={{
                                        borderRadius: 10,
                                        height: 48,
                                        padding: '0 28px',
                                        fontWeight: 600,
                                        fontSize: 15,
                                        background:
                                            searchText.length === 10
                                                ? 'linear-gradient(135deg, #5851D2 0%, #6d66e0 100%)'
                                                : undefined,
                                        border: 'none',
                                        boxShadow:
                                            searchText.length === 10
                                                ? '0 4px 14px rgba(88, 81, 210, 0.35)'
                                                : 'none',
                                        transition: 'all 0.2s ease',
                                        letterSpacing: '0.3px',
                                    }}
                                >
                                    Submit
                                </Button>
                            </div>

                            {errorMessage && (
                                <Alert
                                    message={errorMessage}
                                    type="error"
                                    showIcon
                                    closable
                                    onClose={() => setErrorMessage('')}
                                    style={{ borderRadius: 10, border: '1px solid #fecaca', background: '#fef2f2' }}
                                />
                            )}
                        </Space>
                    </Card>
                ) : (
                    <div>
                        {/* <button
                            onClick={goBackToSearch}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#C9262C',
                                fontSize: '16px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                padding: '8px 12px',
                                marginBottom: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                borderRadius: '8px',
                                transition: 'background 0.2s',
                            }}
                            onMouseOver={(e) => e.currentTarget.style.background = '#f1f5f9'}
                            onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                            <span style={{ fontSize: '20px' }}>←</span> Back
                        </button> */}

                        {selectedOperator === 'fino' && (
                            <FinoImWDMT mobile={verifiedMobile} />
                        )}
                        {selectedOperator === 'airtel' && (
                            <AirtelImwDMT
                                mobile={verifiedMobile}
                                validity={airtelContext.validity}
                                referenceKey={airtelContext.referenceKey}
                                availableLimit={airtelContext.limitAvailable}
                            />
                        )}
                    </div>
                )}
            </div>

            {renderOperatorModal()}

            <style>{`
                .ant-input:focus {
                    box-shadow: none !important;
                }
            `}</style>
        </div>
    );
};

export default IndexComponent;